import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Download, BookOpen, Users, Settings, Route, Truck, Map, Calendar, MessageSquare, BarChart3, HelpCircle, Bot, ChevronDown, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const SECTIONS = [
  {
    id: 'ciudadano',
    icon: Users,
    title: 'Portal Ciudadano',
    color: '#009940',
    items: [
      {
        id: 'crear',
        title: 'Crear una solicitud',
        content: (
          <div className="space-y-4">
            <p className="text-slate-600 text-sm">El portal es accesible para cualquier ciudadano sin necesidad de cuenta. El asistente guía en <strong>5 pasos</strong>:</p>
            <ol className="space-y-3">
              {[
                ['Tipo de enseres', 'Selecciona Muebles/Enseres o Electrodomésticos.'],
                ['Lista de artículos', 'Añade hasta 6 artículos. La IA estima el volumen automáticamente.'],
                ['Ubicación', 'Introduce dirección completa. El sistema geolocaliza la dirección.'],
                ['Datos de contacto', 'Nombre, DNI, email y teléfono. Aceptación obligatoria de privacidad (RGPD).'],
                ['Fecha preferida', 'Elige la fecha deseada. Los festivos y domingos aparecen bloqueados.'],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#009940] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div><span className="font-semibold text-slate-700">{t}:</span> <span className="text-slate-600 text-sm">{d}</span></div>
                </li>
              ))}
            </ol>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
              Al finalizar, el ciudadano recibe un <strong>código de seguimiento</strong> (ej. MAJ-20260605-0012) por pantalla, email y WhatsApp.
            </div>
          </div>
        ),
      },
      {
        id: 'consultar',
        title: 'Consultar el estado',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Desde la página principal → <strong>&ldquo;Consultar mi solicitud&rdquo;</strong>, el ciudadano introduce su código y ve el estado actual.</p>
            <table className="w-full border-collapse text-sm">
              <thead><tr className="bg-[#374B67] text-white"><th className="p-2 text-left rounded-tl-lg">Estado</th><th className="p-2 text-left rounded-tr-lg">Descripción</th></tr></thead>
              <tbody>
                {[
                  ['Notificada','Recibida, pendiente de asignación'],
                  ['Asignada','Incluida en una ruta planificada'],
                  ['En Ruta','El vehículo está en camino'],
                  ['Recogida','Enseres recogidos'],
                  ['Completada','Proceso finalizado'],
                  ['Cancelada','Cancelada por el servicio'],
                ].map(([e, d], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="p-2 font-medium">{e}</td><td className="p-2">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      },
    ],
  },
  {
    id: 'bot',
    icon: Bot,
    title: 'Bot de WhatsApp',
    color: '#25D366',
    items: [
      {
        id: 'bot-ciudadano',
        title: 'Comandos para ciudadanos',
        content: (
          <div className="space-y-4 text-sm text-slate-600">
            <p>Los ciudadanos pueden interactuar con el servicio a través de WhatsApp enviando mensajes al número oficial:</p>
            <table className="w-full border-collapse text-sm">
              <thead><tr className="bg-[#374B67] text-white"><th className="p-2 text-left">Comando</th><th className="p-2 text-left">Acción</th></tr></thead>
              <tbody>
                {[
                  ['NUEVA (o 1)','Inicia el flujo de creación de solicitud por WhatsApp'],
                  ['CONSULTA MAJ-XXXXXXXX','Devuelve el estado de esa solicitud'],
                  ['AYUDA (o 2)','Muestra el menú de opciones'],
                ].map(([c, a], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="p-2 font-mono font-medium text-[#374B67]">{c}</td><td className="p-2">{a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="font-semibold text-slate-700 mt-3">Flujo de creación por WhatsApp (6 pasos):</p>
            <ol className="space-y-2">
              {['Nombre completo','Dirección (calle, número, piso)','Tipo de enseres (1=Muebles, 2=Electrodomésticos)','Lista de artículos separados por coma','Fecha preferida (DD/MM/YYYY)','Confirmar con SI para generar el ticket'].map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#25D366] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        ),
      },
    ],
  },
  {
    id: 'backoffice',
    icon: Settings,
    title: 'Acceso al Backoffice',
    color: '#374B67',
    items: [
      {
        id: 'acceso',
        title: 'Login y contraseña',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Acceso en <strong>/admin/login</strong> con las credenciales asignadas.</p>
            <p>En el primer acceso, el sistema exige cambiar la contraseña.</p>
            <p>Para cambiar la contraseña: <strong>Sidebar izquierdo → icono de llave → Cambiar contraseña</strong> (mínimo 8 caracteres).</p>
            <p>Para cerrar sesión: <strong>Sidebar → Cerrar Sesión</strong>. El token expira automáticamente.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'solicitudes',
    icon: BookOpen,
    title: 'Solicitudes',
    color: '#374B67',
    items: [
      {
        id: 'listado',
        title: 'Listado y filtros',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Listado paginado (20 por página). La columna <strong>F. Preferida</strong> muestra la fecha elegida por el ciudadano para la recogida.</p>
            <p className="font-semibold text-slate-700">Filtros disponibles:</p>
            <ul className="space-y-1">
              {[
                ['Búsqueda libre','Ticket, nombre del ciudadano o dirección'],
                ['Teléfono','Búsqueda parcial, ej: "600" o "91"'],
                ['Zona','Zona Golf · Monte Pilar · Majadahonda Centro'],
                ['Estado','Notificada · Asignada · En Ruta · Recogida · Completada · Cancelada'],
                ['Tipo','Muebles/Enseres · Electrodomésticos'],
              ].map(([f, d]) => (
                <li key={f} className="flex gap-2"><span className="font-medium text-slate-700 w-28 flex-shrink-0">{f}:</span><span>{d}</span></li>
              ))}
            </ul>
            <p className="font-semibold text-slate-700 mt-2">Exportación:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li><strong>XLSX</strong> — Excel con todos los campos</li>
              <li><strong>SHP</strong> — Shapefile comprimido (GIS)</li>
              <li><strong>GPKG</strong> — GeoPackage (OGC)</li>
            </ul>
          </div>
        ),
      },
      {
        id: 'detalle',
        title: 'Detalle y cambio de estado',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Al pulsar <strong>Ver</strong> en una solicitud se accede a la ficha completa con: datos del ciudadano, artículos, dirección en mapa, historial de cambios y notas del administrador.</p>
            <p>El administrador puede <strong>cambiar el estado</strong> y añadir una nota. Al guardar, el ciudadano recibe notificación automática por email y WhatsApp.</p>
          </div>
        ),
      },
      {
        id: 'borrado',
        title: 'Borrado masivo',
        content: (
          <div className="space-y-3">
            <ol className="space-y-3">
              {[
                ['Seleccionar','Marcar casillas en la columna izquierda. El checkbox de la cabecera selecciona toda la página.'],
                ['Pulsar Eliminar','Aparece el botón rojo "Eliminar (N)" en la parte superior. Pulsarlo.'],
                ['Confirmar','Un diálogo informa de cuántas solicitudes se borrarán y que las asignadas a rutas serán desasignadas.'],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                  <div className="text-sm"><span className="font-semibold text-slate-700">{t}:</span> <span className="text-slate-600">{d}</span></div>
                </li>
              ))}
            </ol>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
              El borrado es permanente. Se recomienda exportar los datos antes de eliminar.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'rutas',
    icon: Route,
    title: 'Gestión de Rutas',
    color: '#009940',
    items: [
      {
        id: 'ruta-unica',
        title: 'Ruta Única (recomendada)',
        content: (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Crea automáticamente una ruta con <strong>todas las solicitudes cuya fecha preferida coincide con el día seleccionado</strong>, sin límite de capacidad.</p>
            <ol className="space-y-3">
              {[
                ['Abrir el modal','Pulsar el botón azul "Ruta Única" en Gestión de Rutas.'],
                ['Seleccionar fecha','El contador muestra cuántas solicitudes tienen esa fecha preferida. Se actualiza en tiempo real.'],
                ['Completar datos','Nombre (sugerido automático), turno, vehículo, conductor (opcional) y zona (opcional).'],
                ['Crear','Pulsar "Crear ruta con N paradas". Los ciudadanos reciben notificación inmediata.'],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#009940] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                  <div className="text-sm"><span className="font-semibold text-slate-700">{t}:</span> <span className="text-slate-600">{d}</span></div>
                </li>
              ))}
            </ol>
          </div>
        ),
      },
      {
        id: 'ruta-manual',
        title: 'Ruta Manual',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Selección individual de solicitudes en un proceso de 2 pasos.</p>
            <ul className="space-y-2 list-disc list-inside">
              <li><strong>Paso 1:</strong> Nombre, fecha, turno, vehículo, conductor y zona de filtro.</li>
              <li><strong>Paso 2:</strong> Lista de solicitudes Notificadas. Tarjetas con ticket, zona, dirección y volumen.</li>
              <li><strong>Llenar auto:</strong> rellena hasta la capacidad máxima del vehículo.</li>
              <li><strong>Forzar carga:</strong> actívalo para superar el límite de m³.</li>
            </ul>
          </div>
        ),
      },
      {
        id: 'ruta-ia',
        title: 'Optimización con IA',
        content: (
          <div className="space-y-3 text-sm text-slate-600">
            <p>La IA (Claude · Anthropic) analiza las solicitudes y genera rutas óptimas por proximidad y capacidad.</p>
            <ol className="space-y-2">
              {['Configurar fecha, turno, zona y tiempo por parada.','Pulsar "Generar". El proceso tarda 10-20 segundos.','Revisar las rutas sugeridas con paradas, km y comentarios.','Confirmar y guardar para crear las rutas definitivamente.'].map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#374B67] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        ),
      },
      {
        id: 'ruta-detalle',
        title: 'Detalle, PDF y QR',
        content: (
          <div className="space-y-2 text-sm text-slate-600">
            <ul className="space-y-2">
              {[
                ['Ver detalle','Mapa de paradas, tabla de solicitudes y métricas'],
                ['PDF operativo','Listado de paradas con QR de Google Maps (rutas largas generan varios QR)'],
                ['QR Poster','Cartel A4 con QR para imprimir y colocar en la cabina'],
                ['Publicar','Cambia el estado de Borrador a Publicada'],
                ['Eliminar','Las solicitudes vuelven a estado Notificada'],
              ].map(([a, d]) => (
                <li key={a} className="flex gap-2"><span className="font-medium text-slate-700 w-28 flex-shrink-0">{a}:</span><span>{d}</span></li>
              ))}
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    id: 'vehiculos',
    icon: Truck,
    title: 'Vehículos',
    color: '#374B67',
    items: [{
      id: 'flota',
      title: 'Gestión de la flota',
      content: (
        <div className="space-y-2 text-sm text-slate-600">
          <ul className="space-y-1 list-disc list-inside">
            <li>Listado con matrícula, marca, modelo, capacidad (m³) y estado.</li>
            <li><strong>Añadir vehículo</strong> manualmente con el botón del mismo nombre.</li>
            <li><strong>Importar desde Excel:</strong> descarga la plantilla, complétala y súbela.</li>
            <li>Activar/desactivar vehículos (los inactivos no aparecen al crear rutas).</li>
            <li>Marcar como <strong>Favorito</strong> para que aparezca primero en los selectores.</li>
          </ul>
        </div>
      ),
    }],
  },
  {
    id: 'mapa',
    icon: Map,
    title: 'Mapa interactivo',
    color: '#69C7EA',
    items: [{
      id: 'mapa-uso',
      title: 'Uso del mapa',
      content: (
        <div className="space-y-2 text-sm text-slate-600">
          <p>Visualización geográfica sobre OpenStreetMap con filtros por zona y estado.</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Puntos de color por estado: azul (Notificada), morado (Asignada), naranja (En Ruta), verde (Completada).</li>
            <li>Clic en un punto → ticket, zona, estado y tipo.</li>
            <li>Capa de rutas publicadas con trazados.</li>
          </ul>
        </div>
      ),
    }],
  },
  {
    id: 'festivos',
    icon: Calendar,
    title: 'Festivos',
    color: '#F59E0B',
    items: [{
      id: 'festivos-config',
      title: 'Configurar festivos',
      content: (
        <div className="space-y-2 text-sm text-slate-600">
          <p>Los domingos se bloquean automáticamente. Esta sección añade festivos locales o días de corte de servicio.</p>
          <ul className="space-y-1 list-disc list-inside">
            <li><strong>Añadir:</strong> seleccionar fecha en el calendario → pulsar &ldquo;Añadir Festivo&rdquo;.</li>
            <li><strong>Eliminar:</strong> pulsar la X junto a la fecha en el listado.</li>
            <li>Los festivos aparecen deshabilitados en el wizard ciudadano.</li>
          </ul>
        </div>
      ),
    }],
  },
  {
    id: 'whatsapp',
    icon: MessageSquare,
    title: 'WhatsApp Logs',
    color: '#25D366',
    items: [{
      id: 'logs',
      title: 'Registro de mensajes',
      content: (
        <div className="space-y-2 text-sm text-slate-600">
          <p>Registro completo de todos los mensajes: entrantes (ciudadanos) y salientes (notificaciones automáticas).</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Filtro por dirección: Entrantes / Salientes.</li>
            <li>Datos: teléfono, contenido, ticket asociado, estado Twilio y fecha/hora.</li>
            <li>Tipos de salientes: confirmación, actualización de estado, asignación de ruta.</li>
          </ul>
        </div>
      ),
    }],
  },
  {
    id: 'historico',
    icon: BarChart3,
    title: 'Histórico y Exportaciones',
    color: '#374B67',
    items: [{
      id: 'historico-uso',
      title: 'Histórico y Resumen Diario',
      content: (
        <div className="space-y-3 text-sm text-slate-600">
          <p>Consulta solicitudes por rango de fechas, zona, estado y tipo. Exportable en:</p>
          <table className="w-full border-collapse text-sm">
            <thead><tr className="bg-[#374B67] text-white"><th className="p-2 text-left">Formato</th><th className="p-2 text-left">Uso recomendado</th></tr></thead>
            <tbody>
              {[['XLSX','Excel, análisis e informes'],['SHP','ArcGIS, QGIS'],['GPKG','GIS con soporte SQL espacial'],['PDF','Archivo y presentaciones']].map(([f, u], i) => (
                <tr key={f} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="p-2 font-mono font-bold">{f}</td><td className="p-2">{u}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2">El <strong>Resumen Diario</strong> ofrece KPIs para un rango personalizable: solicitudes, completadas, rutas y km. Exportable a PDF y XLSX.</p>
        </div>
      ),
    }],
  },
  {
    id: 'faq',
    icon: HelpCircle,
    title: 'Preguntas Frecuentes',
    color: '#374B67',
    items: [{
      id: 'faq-lista',
      title: 'FAQ',
      content: (
        <div className="space-y-4 text-sm">
          {[
            ['¿Qué pasa si el ciudadano no tiene email?', 'Las notificaciones se envían solo por WhatsApp si hay teléfono. Si tampoco tiene, debe guardar el código de pantalla.'],
            ['¿Se pueden añadir solicitudes a una ruta ya creada?', 'Sí, desde el detalle de la ruta. También se puede eliminar y recrear con Ruta Única o Manual.'],
            ['¿Qué ocurre si elimino una ruta?', 'Las solicitudes vuelven a estado Notificada y pueden asignarse de nuevo.'],
            ['¿La Ruta Única incluye solicitudes de todos los días?', 'No. Solo las que tienen fecha preferida igual a la fecha seleccionada en el modal.'],
            ['¿Cómo borrar solicitudes de prueba?', 'Usar el filtro de teléfono con el prefijo del número de prueba (ej. "000"). Seleccionar con checkboxes y usar el botón Eliminar.'],
          ].map(([q, a]) => (
            <div key={q} className="border-l-2 border-[#69C7EA] pl-3">
              <p className="font-semibold text-slate-700">{q}</p>
              <p className="text-slate-600 mt-0.5">{a}</p>
            </div>
          ))}
        </div>
      ),
    }],
  },
];

function AccordionSection({ section, isOpen, onToggle }) {
  const Icon = section.icon;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
        data-testid={`manual-section-${section.id}`}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${section.color}18` }}>
          <Icon size={18} style={{ color: section.color }} />
        </div>
        <span className="flex-1 text-left font-semibold text-slate-800">{section.title}</span>
        {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {section.items.map(item => (
            <div key={item.id} className="px-5 py-4">
              <h4 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: section.color }}></span>
                {item.title}
              </h4>
              {item.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminManual() {
  const [openSections, setOpenSections] = useState(new Set(['ciudadano']));
  const [downloading, setDownloading] = useState(false);

  const toggle = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(SECTIONS.map(s => s.id)));
  const collapseAll = () => setOpenSections(new Set());

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/admin/manual/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Enseres_Majadahonda.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado correctamente');
    } catch {
      toast.error('Error al descargar el manual');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="bg-[#374B67] rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold">Manual de Usuario</h1>
              <p className="text-white/70 text-sm mt-1">Sistema de Recogida de Enseres · Ayuntamiento de Majadahonda</p>
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 bg-[#009940] hover:bg-[#007A33] text-white px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60 transition-colors flex-shrink-0"
              data-testid="manual-download-pdf-button"
            >
              <Download size={16} />
              {downloading ? 'Generando PDF...' : 'Descargar PDF'}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-slate-500">{SECTIONS.length} secciones</span>
          <span className="text-slate-300">·</span>
          <button onClick={expandAll} className="text-sm text-[#374B67] font-medium hover:underline">Expandir todo</button>
          <span className="text-slate-300">·</span>
          <button onClick={collapseAll} className="text-sm text-[#374B67] font-medium hover:underline">Colapsar todo</button>
        </div>

        {/* Sections */}
        {SECTIONS.map(section => (
          <AccordionSection
            key={section.id}
            section={section}
            isOpen={openSections.has(section.id)}
            onToggle={() => toggle(section.id)}
          />
        ))}

        <p className="text-center text-xs text-slate-400 mt-6">
          Valoriza Servicios Medioambientales · Ayuntamiento de Majadahonda · v1.2
        </p>
      </div>
    </AdminLayout>
  );
}
